// ═══════════════════════════════════════════════════════════════
// VaultZkTotpSetup.js — Activation 2FA TOTP V2 (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Génère un secret 20 bytes, l'affiche en base32 + URI otpauth (que
// l'user copie/scanne dans son authenticator), demande un code de
// validation, puis chiffre le secret avec la KEK passphrase et POST
// au backend. Le secret n'est JAMAIS lisible côté serveur en clair.
//
// Pas de QR généré (pas de lib externe). L'user copie le secret base32
// ou clique le lien otpauth:// (mobile authenticator pickup).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { Smartphone, Copy, Check, Loader2, ShieldCheck } from 'lucide-react';
import {
    generateTotpSecret, bytesToBase32, buildOtpAuthUrl,
    verifyTotpCode, wrapTotpSecret,
} from '../../utils/vaultZkTotp';
import { deriveKEKFromPassphrase } from '../../utils/vaultZkCrypto';
import { totpSetup } from '../../services/vaultZkApi';
import { useVaultZk } from './VaultZkContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 8, 18, 0.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: (isDark) => ({
        width: 'min(480px, 100%)', background: isDark ? '#13131a' : '#ffffff',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        borderRadius: 16, padding: '28px 32px 24px',
        boxShadow: '0 24px 48px rgba(76, 29, 149, 0.35)',
        border: isDark ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.18)',
    }),
    title: { fontSize: 17, fontWeight: 600, margin: '6px 0 4px' },
    sub: (isDark) => ({ fontSize: 13, color: isDark ? '#9090a8' : '#6a6a85', margin: '0 0 18px', lineHeight: 1.55 }),
    secretBox: (isDark) => ({
        padding: '14px 16px', borderRadius: 10, marginBottom: 14,
        background: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.25)',
        fontFamily: 'monospace', fontSize: 15, letterSpacing: 1.5, wordBreak: 'break-all',
    }),
    label: { fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'block' },
    input: (isDark) => ({
        width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
        background: isDark ? '#1c1c28' : '#f7f7fb',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 8, outline: 'none', fontFamily: 'inherit',
    }),
    pwdInput: (isDark) => ({
        width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13,
        background: isDark ? '#1c1c28' : '#f7f7fb',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 7, outline: 'none', fontFamily: 'inherit',
    }),
    btnPrimary: (busy) => ({
        width: '100%', padding: '11px 14px',
        background: busy ? '#6d28d9' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        color: '#fff', border: 'none', borderRadius: 9,
        fontSize: 14, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        marginTop: 6,
    }),
    btnGhost: (isDark) => ({
        padding: '9px 14px', background: 'transparent',
        color: isDark ? '#b8b8c8' : '#5a5a72',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 9, fontSize: 13, cursor: 'pointer',
    }),
    err: { color: '#ef4444', fontSize: 12.5, marginTop: 6, marginBottom: 8 },
    copyBtn: { background: 'transparent', border: '1px solid rgba(139, 92, 246, 0.4)',
        color: '#8b5cf6', padding: '6px 11px', borderRadius: 7, fontSize: 12.5,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
};

export default function VaultZkTotpSetup({ challenge, accountLabel, onClose, onSuccess }) {
    const { refreshChallenge } = useVaultZk();
    const { isDark } = useTheme();
    const [secret, setSecret] = useState(null);
    const [secretB32, setSecretB32] = useState('');
    const [otpAuthUrl, setOtpAuthUrl] = useState('');
    const [code, setCode] = useState('');
    const [pwd, setPwd] = useState('');
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        const s = generateTotpSecret();
        setSecret(s);
        const b32 = bytesToBase32(s);
        setSecretB32(b32);
        setOtpAuthUrl(buildOtpAuthUrl({ accountLabel: accountLabel || 'coffre', secretB32: b32 }));
    }, [accountLabel]);

    async function copySecret() {
        try {
            await navigator.clipboard.writeText(secretB32);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* noop */ }
    }

    async function activate() {
        setErr('');
        if (!/^\d{6}$/.test(code)) { setErr('Saisissez un code à 6 chiffres.'); return; }
        if (!pwd) { setErr('Re-saisissez votre passphrase pour chiffrer le secret 2FA.'); return; }
        setBusy(true);
        try {
            const ok = await verifyTotpCode(secret, code);
            if (!ok) {
                setErr('Code 2FA incorrect — réessayez avec le code en cours dans votre authenticator.');
                return;
            }
            // Re-dérive la KEK depuis la passphrase pour wrap le secret
            const kek = await deriveKEKFromPassphrase(pwd, challenge.salt, challenge.iter);
            const blob = await wrapTotpSecret(secret, kek);
            await totpSetup(blob);
            await refreshChallenge();
            toast.success('2FA activé sur le coffre-fort');
            if (onSuccess) onSuccess();
            if (onClose) onClose();
        } catch (e) {
            setErr(`Activation 2FA refusée : ${e.message || 'erreur'}`);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={styles.overlay} role="dialog" aria-modal="true">
            <div style={styles.modal(isDark)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ShieldCheck size={20} color="#8b5cf6" />
                    <h2 style={styles.title}>Activer la 2FA (TOTP)</h2>
                </div>
                <p style={styles.sub(isDark)}>
                    Ajoutez une seconde clé : un code à 6 chiffres généré par votre app authenticator
                    (Google Authenticator, 1Password, Bitwarden, Authy…). Le secret est chiffré par votre
                    passphrase — illisible côté serveur.
                </p>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                    <Smartphone size={14} color="#8b5cf6" />
                    <strong style={{ fontSize: 13 }}>Ajoutez ce secret dans votre app</strong>
                </div>

                <div style={styles.secretBox(isDark)}>{secretB32 || '...'}</div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                    <button type="button" style={styles.copyBtn} onClick={copySecret}>
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? 'Copié' : 'Copier le secret'}
                    </button>
                    <a
                        href={otpAuthUrl}
                        style={{ ...styles.copyBtn, textDecoration: 'none' }}
                        title="Ouvrir dans votre app authenticator (mobile)"
                    >
                        Ouvrir dans l'app
                    </a>
                </div>

                <label style={styles.label}>Code 2FA actuel (6 chiffres)</label>
                <input
                    type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    style={{ ...styles.input(isDark), letterSpacing: 4, fontSize: 18, textAlign: 'center', marginBottom: 12 }}
                />

                <label style={styles.label}>Re-saisissez votre passphrase</label>
                <input
                    type="password" value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="Pour chiffrer localement le secret 2FA"
                    style={{ ...styles.pwdInput(isDark), marginBottom: 14 }}
                />

                {err && <div style={styles.err}>{err}</div>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button style={styles.btnGhost(isDark)} onClick={onClose}>Annuler</button>
                    <button style={{ ...styles.btnPrimary(busy), width: 'auto', minWidth: 180 }} onClick={activate} disabled={busy}>
                        {busy ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />}
                        Activer la 2FA
                    </button>
                </div>
            </div>
        </div>
    );
}
